import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useTranslation } from 'react-i18next';
import { serviceDefinitions } from './serviceDefinitions';
import { handleExternalLinkClick } from '../../utils/externalLinkConfirm';

export default function MoveOutServiceDialogContent() {
  const { t } = useTranslation();
  const theme = useTheme();
  const service = serviceDefinitions.move_out_clean;
  const rutInfoLinkHref =
    'https://www.skatteverket.se/privat/fastigheterochbostad/rotarbeteochrutarbete/safungerarrutavdraget.4.d5e04db14b6fef2c866097.html';
  const includedItems = t('services.move_out_clean_sections.included.items', {
    returnObjects: true,
  });
  const preparationItems = t(
    'services.move_out_clean_sections.preparation.items',
    {
      returnObjects: true,
    },
  );
  const priceItems = t('services.move_out_clean_prices.items', {
    returnObjects: true,
  });
  const includedList = Array.isArray(includedItems) ? includedItems : [];
  const preparationList = Array.isArray(preparationItems)
    ? preparationItems
    : [];
  const priceList = Array.isArray(priceItems) ? priceItems : [];

  const renderCompactList = (items: string[]) => (
    <List disablePadding>
      {items.map((item) => (
        <ListItem key={item} disableGutters sx={{ py: 0.25 }}>
          <ListItemIcon sx={{ minWidth: 30 }}>
            <CheckCircleOutlineIcon
              fontSize='small'
              sx={{ color: theme.palette.secondary.main }}
            />
          </ListItemIcon>
          <ListItemText
            primary={item}
            primaryTypographyProps={{
              variant: 'body2',
              sx: { color: '#333', fontWeight: 500 },
            }}
          />
        </ListItem>
      ))}
    </List>
  );

  const sectionCardSx = {
    p: 2,
    borderRadius: 2,
    border: '1px solid rgba(45, 0, 84, 0.12)',
    backgroundColor: '#fff',
  };

  return (
    <>
      <Typography
        variant='subtitle2'
        sx={{ color: '#555', lineHeight: 1.75, mb: 2.5 }}
      >
        {t(service.detailKey)}
      </Typography>
      <Typography variant='h5' sx={{ fontWeight: 700, mb: 1 }}>
        {t('services.move_out_clean_info_title')}
      </Typography>
      <Typography
        variant='body1'
        sx={{ color: '#555', lineHeight: 1.75, mb: 2.5 }}
      >
        {t('services.move_out_clean_intro')}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 1.5,
        }}
      >
        <Box sx={{ ...sectionCardSx, gridColumn: { xs: '1', md: '1 / -1' } }}>
          <Typography variant='subtitle1' sx={{ fontWeight: 700, mb: 0.75 }}>
            {t('services.includes')}
          </Typography>
          {renderCompactList(includedList)}
          <Typography
            component='a'
            href={rutInfoLinkHref}
            target='_blank'
            rel='noreferrer'
            onClick={(event) =>
              handleExternalLinkClick(event, t('common.leave_site_confirm'))
            }
            variant='subtitle2'
            sx={{
              display: 'inline-block',
              color: theme.palette.primary.main,
              lineHeight: 1.7,
              mt: 0.75,
              fontWeight: 700,
              textDecoration: 'underline',
              textUnderlineOffset: '2px',
              '&:hover': { opacity: 0.85 },
            }}
          >
            {t('services.move_out_clean_rut_link')}
          </Typography>
        </Box>

        <Box sx={{ ...sectionCardSx, gridColumn: { xs: '1', md: '1 / -1' } }}>
          <Typography variant='subtitle1' sx={{ fontWeight: 700, mb: 0.75 }}>
            {t('services.move_out_clean_sections.preparation.title')}
          </Typography>
          {renderCompactList(preparationList)}
        </Box>

        <Box
          sx={{
            ...sectionCardSx,
            gridColumn: { xs: '1', md: '1 / -1' },
            backgroundColor: 'rgba(45, 0, 84, 0.03)',
          }}
        >
          <Typography variant='subtitle1' sx={{ fontWeight: 700, mb: 0.75 }}>
            {t('services.move_out_clean_prices.title')}
          </Typography>
          {priceList.map((item, index) => (
            <Box
              key={`move-out-price-${index}`}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 2,
                py: 1,
                borderBottom:
                  index === priceList.length - 1
                    ? 'none'
                    : '1px dashed rgba(45, 0, 84, 0.2)',
              }}
            >
              <Typography
                variant='body2'
                sx={{ color: '#333', fontWeight: 600 }}
              >
                {item.label}
              </Typography>
              <Typography
                variant='body2'
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                  textAlign: 'right',
                  flexShrink: 0,
                  maxWidth: '55%',
                }}
              >
                {item.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </>
  );
}
